import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../data/api";
import { Button, Card, Input, useDivisionMode } from "../components/ui";
import type { Route, RouteStopRow, SupervisorTeamMember } from "../data/schemaTypes";
import { stopDisplayName, stopHash, stopShortLabel } from "../data/stopLabel";
import { useAuth } from "../auth/AuthContext";
import {
  useLiveFleet,
  driverProgress,
  driverAlert,
  type LiveVehicle,
} from "../features/lastmile/useLiveFleet";
import { SupervisorFleetMap } from "../features/lastmile/SupervisorFleetMap";
import {
  driverPcts,
  fleetTotals,
  formatPct,
  pctBadgeClasses,
} from "../features/lastmile/fleetStats";

type ViewMode = "list" | "map";

// SupervisorPage — dashboard de avance del día. Muestra:
//   - KPIs globales: efectividad de visita / entrega, programados,
//     totales, visitados, efectivos, rechazos.
//   - Cards por repartidor con: nombre/código, totales (total/efectivas/
//     rechazadas), % efectividad de visita/entrega con badge coloreado,
//     hora de inicio y fin del recorrido, y acciones (Ver Mapa, Ver
//     Clientes Rechazados).
// El operador puede cambiar la fecha consultada con el datepicker;
// "Actualizar" forza un refresh inmediato fuera del polling.
export function SupervisorPage() {
  // El modo (CONSUMO/SALUD) lo controla el sidebar. Ambos casos usan
  // el mismo layout — el hook `useLiveFleet` se encarga de traer la
  // data correcta según el modo. Para SALUD, devuelve rutas
  // self-dispatch de repartidores en los últimos 3 días con shape
  // `LiveVehicle` (plate=DNI, driverFullName=nombre del repartidor).
  const [division] = useDivisionMode();
  // Fecha local del día actual en YYYY-MM-DD — evita el shift por UTC
  // que produce `new Date().toISOString().slice(0, 10)`.
  const [plannedDate, setPlannedDate] = useState<string>(() => formatLocalDate(new Date()));

  // Drill-down desde la vista del coordinador: ?supervisor=<uuid>.
  // Cargamos sus warehouses y los pasamos a useLiveFleet para filtrar.
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const supervisorIdParam = searchParams.get("supervisor");
  const [drillSupervisor, setDrillSupervisor] = useState<SupervisorTeamMember | null>(null);
  useEffect(() => {
    if (!supervisorIdParam) {
      setDrillSupervisor(null);
      return;
    }
    // Solo coordinator/admin pueden hacer drill-down — para un supervisor
    // mirándose a sí mismo, el filtro ya viene del scope server-side.
    if (currentUser?.role !== "coordinator" && currentUser?.role !== "admin") {
      setDrillSupervisor(null);
      return;
    }
    let cancelled = false;
    api
      .getCoordinatorTeam()
      .then((team) => {
        if (cancelled) return;
        setDrillSupervisor(team.find((s) => s.user_id === supervisorIdParam) ?? null);
      })
      .catch(() => {
        if (!cancelled) setDrillSupervisor(null);
      });
    return () => {
      cancelled = true;
    };
  }, [supervisorIdParam, currentUser?.role]);

  const warehouseIds = useMemo(
    () => drillSupervisor?.warehouses.map((w) => w.id),
    [drillSupervisor],
  );
  const { vehicles, loading, error, lastRefresh, refresh } = useLiveFleet(
    20_000,
    plannedDate,
    warehouseIds,
    division,
  );
  const [search, setSearch] = useState("");
  // Drawer state: además del vehículo seleccionado guardamos el modo
  // con el que se abrió — "all" muestra toda la ruta; "failed" filtra
  // a paradas rechazadas/omitidas para investigar motivos en el botón
  // "Ver Clientes Rechazados".
  const [selected, setSelected] = useState<{
    driver: LiveVehicle;
    mode: "all" | "failed";
  } | null>(null);
  const [view, setView] = useState<ViewMode>("list");
  // En modo mapa el vehículo destacado aparece en un panel inferior. Lo
  // mantenemos como id (no LiveVehicle) para que sobreviva al refresh.
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // `vehicles` ya viene filtrado a los que tienen ruta (`useLiveFleet`
  // solo devuelve vehículos con ruta del día). Buscamos por placa o
  // chofer y ordenamos por eficiencia de visita (entregas exitosas /
  // pedidos visitados) descendente. Los que aún no visitaron a nadie
  // (visited = 0) van al final con orden estable por placa para que
  // no salten entre polls.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = !q
      ? vehicles
      : vehicles.filter(
          (v) =>
            v.plate.toLowerCase().includes(q) ||
            (v.driverFullName ?? "").toLowerCase().includes(q) ||
            (v.driverCode ?? "").toLowerCase().includes(q),
        );
    // Ordenamos por **eficiencia de visita** = visited / total. Es el
    // KPI principal del header (driverPcts.efVisita) y mide cuánto del
    // plan ya se atendió. El que más avanzó queda primero — la grilla
    // se llena de izquierda a derecha y luego hacia abajo siguiendo ese
    // orden. Vehículos con total = 0 (sin ruta cargada) caen al final.
    const efVisita = (v: LiveVehicle): number => {
      if (v.stopsTotal === 0) return -1;
      return (v.stopsDone + v.stopsFailed) / v.stopsTotal;
    };
    return [...filtered].sort((a, b) => {
      const ea = efVisita(a);
      const eb = efVisita(b);
      if (ea === -1 && eb === -1) return a.plate.localeCompare(b.plate);
      if (ea === -1) return 1; // sin total al final
      if (eb === -1) return -1;
      if (eb !== ea) return eb - ea; // mayor eficiencia primero
      return a.plate.localeCompare(b.plate);
    });
  }, [vehicles, search]);

  // KPIs globales: ver `fleetStats.fleetTotals` para definiciones.
  const totals = useMemo(() => fleetTotals(vehicles), [vehicles]);

  if (loading && vehicles.length === 0) {
    return <Centered title="Cargando avance…" />;
  }
  if (error && vehicles.length === 0) {
    return (
      <Centered title="No se pudo cargar">
        <p className="text-sm text-sub">{error}</p>
      </Centered>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      {/* Banner de drill-down — solo visible cuando el coordinador entró
          a la vista de un supervisor específico. Da contexto y permite
          volver al listado del equipo sin perder estado. */}
      {drillSupervisor && (
        <div className="flex items-center justify-between gap-3 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] uppercase tracking-wide text-sub">
              Viendo equipo de
            </span>
            <span className="font-semibold text-ink truncate">
              {drillSupervisor.full_name}
            </span>
            {drillSupervisor.warehouses.length > 0 && (
              <span className="text-xs text-sub font-mono">
                · {drillSupervisor.warehouses.map((w) => w.code || w.name).join(", ")}
              </span>
            )}
          </div>
          <Link
            to="/coordinador"
            className="text-xs text-primary hover:underline shrink-0"
          >
            ← Volver al equipo
          </Link>
        </div>
      )}

      {/* Header: fecha + acción actualizar + búsqueda */}
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-ink">
            {drillSupervisor ? `Flota de ${drillSupervisor.full_name}` : "Avance del día"}
          </h1>
          <p className="text-xs text-sub mt-0.5">
            {lastRefresh
              ? `Última actualización ${lastRefresh.toLocaleTimeString("es-PE")} · auto-refresh cada 20s`
              : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* El selector de fecha aplica SOLO al modo CONSUMO — en SALUD
              el avance es un rango fijo (últimos 3 días) porque los
              pedidos del repartidor se entregan a lo largo de varios
              días. Mostramos un pill informativo en lugar del date
              picker para que el operador no piense que está roto. */}
          {division === "SALUD" ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-border bg-page text-sub">
              <span aria-hidden>📅</span>
              <span>Últimos 3 días</span>
            </div>
          ) : (
            <input
              type="date"
              value={plannedDate}
              onChange={(e) => setPlannedDate(e.target.value)}
              className="border border-border rounded px-2 py-1.5 text-sm"
              title="Cambiá la fecha para ver el avance histórico de ese día"
            />
          )}
          {loading && (
            <span className="text-[11px] text-sub italic">cargando…</span>
          )}
          <Button variant="primary" onClick={refresh}>
            Actualizar
          </Button>
        </div>
      </header>

      {/* KPIs globales — dos filas para no apilar 7 numeritos minúsculos */}
      <Card padding="md">
        <div className="grid grid-cols-3 gap-3 pb-2 border-b border-border">
          <Kpi label="Ef. Visita"     value={formatPct(totals.efVisita)} />
          <Kpi label="Entregas Efec." value={formatPct(totals.efEntrega)} />
          <Kpi label="Programadas"    value={String(totals.programadas)} unit={totals.programadas === 1 ? "ruta" : "rutas"} />
        </div>
        <div className="grid grid-cols-4 gap-3 pt-2">
          <Kpi label="Total"      value={String(totals.stops)}   unit="órdenes" />
          <Kpi label="Visitados"  value={String(totals.visited)} unit="órdenes" />
          <Kpi label="Efectivas"  value={String(totals.done)}    unit="órdenes" />
          <Kpi label="Rechazados" value={String(totals.failed)}  unit="órdenes" accent={totals.failed > 0} />
        </div>
      </Card>

      {/* Búsqueda + toggle Lista/Mapa + indicador de alertas */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Input
            placeholder="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
          <ViewToggle value={view} onChange={setView} />
        </div>
        {totals.alerts > 0 && (
          <span className="text-xs text-state-failed">
            {totals.alerts} chofer{totals.alerts === 1 ? "" : "es"} con alerta
          </span>
        )}
      </div>

      {/* Cuerpo: lista o mapa */}
      {rows.length === 0 ? (
        <Centered title="Sin rutas en curso para esta fecha">
          <p className="text-xs text-sub mt-2">
            Filtrando por fecha <span className="font-mono font-semibold text-ink">{plannedDate}</span>.
            {" "}Si esperabas ver vehículos, probá cambiar la fecha del selector
            arriba o asegurate de haber importado el plan para hoy.
          </p>
          <div className="flex items-center gap-2 mt-3 justify-center">
            <Button
              variant="secondary"
              onClick={() => setPlannedDate(formatLocalDate(new Date()))}
            >
              Ver hoy
            </Button>
            <Link
              to="/programacion"
              className="inline-block bg-primary hover:bg-primary-hover text-white text-sm font-medium rounded-md px-3 py-1.5"
            >
              Ir a Programación
            </Link>
          </div>
        </Centered>
      ) : view === "list" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((d) => (
            <DriverCard
              key={d.id}
              d={d}
              onOpen={(mode) => setSelected({ driver: d, mode })}
            />
          ))}
        </div>
      ) : (
        <FleetMapView
          vehicles={rows}
          focusedId={focusedId}
          onSelect={setFocusedId}
          onOpenDetail={(d) => setSelected({ driver: d, mode: "all" })}
        />
      )}

      {selected && (
        <DriverDrawer
          driver={selected.driver}
          onClose={() => setSelected(null)}
          onRefresh={refresh}
          showOnlyFailed={selected.mode === "failed"}
        />
      )}
    </div>
  );
}

// ---------------- Toggle vista Lista / Mapa ----------------

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const base = "px-3 py-1.5 text-sm font-medium transition-colors";
  const active = "bg-primary text-white";
  const inactive = "bg-card text-sub hover:text-ink";
  return (
    <div className="inline-flex rounded border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => onChange("list")}
        className={`${base} ${value === "list" ? active : inactive}`}
        aria-pressed={value === "list"}
      >
        Lista
      </button>
      <button
        type="button"
        onClick={() => onChange("map")}
        className={`${base} ${value === "map" ? active : inactive} border-l border-border`}
        aria-pressed={value === "map"}
      >
        Mapa
      </button>
    </div>
  );
}

// ---------------- Vista mapa de flota ----------------

// Mapa con todos los choferes filtrados + panel inferior "Detalle de
// Transporte" cuando se selecciona uno. Equivalente al modal de la
// captura de Drivin: la información clave queda visible sin tapar el
// mapa.
function FleetMapView({
  vehicles,
  focusedId,
  onSelect,
  onOpenDetail,
}: {
  vehicles: LiveVehicle[];
  focusedId: string | null;
  onSelect: (id: string | null) => void;
  onOpenDetail: (d: LiveVehicle) => void;
}) {
  const focused = focusedId ? vehicles.find((d) => d.id === focusedId) ?? null : null;
  // Conteos para el banner informativo del mapa. GPS desactivado, así
  // que solo hay 3 buckets:
  //   - operating: con al menos un POD del día → verde en la ubicación
  //     del último pedido atendido.
  //   - stale: sin POD pero con almacén → dashed gris en el almacén.
  //   - invisible: sin POD ni almacén → fuera del mapa.
  const counts = useMemo(() => {
    let operating = 0;
    let stale = 0;
    let invisible = 0;
    for (const d of vehicles) {
      const hasLastStop = d.lastStopLat != null && d.lastStopLon != null;
      const hasWh = d.warehouseLat != null && d.warehouseLon != null;
      if (hasLastStop) operating++;
      else if (hasWh) stale++;
      else invisible++;
    }
    return { operating, stale, invisible };
  }, [vehicles]);

  return (
    <div className="relative h-[calc(100vh-22rem)] min-h-[400px]">
      <Card padding="none" className="h-full overflow-hidden">
        <SupervisorFleetMap
          vehicles={vehicles}
          selectedId={focusedId ?? undefined}
          onSelect={(id) => onSelect(id)}
        />
      </Card>

      <div className="absolute top-2 left-2 bg-card border border-border rounded px-2 py-1 text-xs text-sub shadow space-y-0.5">
        {counts.operating > 0 && (
          <div>
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#2E7D32] border border-white align-middle mr-1.5" />
            {counts.operating} con entregas — en su último pedido atendido
          </div>
        )}
        {counts.stale > 0 && (
          <div>
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#90A4AE] border border-dashed border-ink/40 align-middle mr-1.5" />
            {counts.stale} sin operación — ubicado{counts.stale === 1 ? "" : "s"} en su almacén
          </div>
        )}
        {counts.invisible > 0 && (
          <div>
            {counts.invisible} sin ubicación posible — no en el mapa
          </div>
        )}
      </div>

      {focused && (
        <FleetMapDetailPanel
          d={focused}
          onClose={() => onSelect(null)}
          onOpenDetail={() => onOpenDetail(focused)}
        />
      )}
    </div>
  );
}

// Panel flotante en la parte inferior — replica "Detalle de Transporte"
// de la imagen de referencia: código, rechazadas, sede, nombre completo,
// rol, hora última lectura GPS y total/efectivas.
function FleetMapDetailPanel({
  d,
  onClose,
  onOpenDetail,
}: {
  d: LiveVehicle;
  onClose: () => void;
  onOpenDetail: () => void;
}) {
  return (
    <div className="absolute bottom-2 left-2 right-2 bg-card border border-border rounded-lg shadow-lg">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-page/60">
        <div className="text-sm font-semibold text-ink">Detalle de Transporte</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="text-sub hover:text-ink text-xl leading-none"
        >
          ×
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 text-sm">
        <DetailField label="Placa" value={d.plate} highlight />
        <DetailField label="Rechazadas" value={String(d.stopsFailed)} highlight={d.stopsFailed > 0} danger={d.stopsFailed > 0} />
        <DetailField label="Ruta" value={d.routeCode ?? "—"} />
        <DetailField label="Total / Efectivas" value={`${d.stopsTotal} / ${d.stopsDone}`} highlight />
        <DetailField
          label="Chofer"
          value={d.driverFullName || "Sin chofer asignado"}
          className="col-span-2"
        />
        <DetailField
          label="Hora — Última Vez"
          value={d.lastGpsAt ? d.lastGpsAt.toLocaleTimeString("es-PE", { hour12: false }) : "—"}
        />
        <div className="flex items-end justify-end">
          <Button variant="primary" onClick={onOpenDetail}>
            Ver detalle
          </Button>
        </div>
      </div>
    </div>
  );
}

function DetailField({
  label,
  value,
  highlight,
  danger,
  className,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  danger?: boolean;
  className?: string;
}) {
  const valColor = danger ? "text-state-failed" : highlight ? "text-primary" : "text-ink";
  return (
    <div className={className}>
      <div className={`text-base font-semibold ${valColor} truncate`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-sub">{label}</div>
    </div>
  );
}

// ---------------- Card por repartidor ----------------

function DriverCard({
  d,
  onOpen,
}: {
  d: LiveVehicle;
  onOpen: (mode: "all" | "failed") => void;
}) {
  const { efVisita, efEntrega } = driverPcts(d);
  const alert = driverAlert(d);
  const pctClass = pctBadgeClasses(efVisita);

  return (
    <Card
      padding="md"
      className={alert !== "none" ? "border-l-4 border-l-state-failed" : ""}
    >
      <div className="flex items-start gap-3">
        {/* Avatar genérico */}
        <div className="w-12 h-12 rounded-full bg-page flex items-center justify-center shrink-0 text-sub">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-8 1.67-8 5v1h16v-1c0-3.33-4.67-5-8-5Z" />
          </svg>
        </div>

        {/* Identificador (placa) + chofer asignado */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-primary truncate font-mono">
            {d.plate}
          </div>
          <div className="text-xs text-sub truncate">
            {d.driverFullName ?? <span className="italic">Sin chofer asignado</span>}
          </div>
          <div className="mt-2 flex items-baseline gap-5 text-sm">
            <Metric n={d.stopsTotal} label="Total" />
            <Metric n={d.stopsDone} label="Efectivas" />
            <Metric n={d.stopsFailed} label="Rechazadas" />
          </div>
        </div>

        {/* Badges de eficiencia */}
        <div className="text-right shrink-0">
          <div className={`px-2.5 py-1 rounded text-sm font-semibold ${pctClass}`}>
            {formatPct(efVisita)}
          </div>
          <div className="text-[10px] text-sub mt-0.5">% Ef. Visita</div>
          <div className="text-sm font-semibold text-ink mt-2">{formatPct(efEntrega)}</div>
          <div className="text-[10px] text-sub mt-0.5">% Ef. Entrega</div>
        </div>
      </div>

      {/* Horarios + acciones — usamos el timestamp del primer y último
          POD del día como "hora real de operación". Es más fiel a la
          actividad del chofer que routeStartedAt/FinishedAt, que solo
          se llenan si el chofer marca el inicio/fin explícito. Si aún
          no atendió a nadie, mostramos "—". */}
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
        <div className="text-xs text-sub leading-tight">
          <div>
            <span className="text-ink font-medium">Hora Inicio:</span>{" "}
            {formatHHMMSS(d.firstStopAt)}
          </div>
          <div>
            <span className="text-ink font-medium">Hora Fin:</span>{" "}
            {formatHHMMSS(d.lastStopAt)}
          </div>
          {d.stopsFailed > 0 && (
            <button
              type="button"
              onClick={() => onOpen("failed")}
              className="mt-1 text-primary hover:text-primary-hover underline text-xs"
            >
              Ver Clientes Rechazados
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {d.routeId && (
            <Link to={`/tracking?vehicle=${encodeURIComponent(d.id)}`}>
              <Button variant="secondary">Ver mapa</Button>
            </Link>
          )}
          <Button variant="primary" onClick={() => onOpen("all")}>
            Detalle
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Metric({ n, label }: { n: number; label: string }) {
  return (
    <div className="text-center">
      <div className="text-base font-semibold text-ink tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
    </div>
  );
}

// ---------------- Drawer detalle (re-usado de la versión anterior) ----------------

function DriverDrawer({
  driver,
  onClose,
  showOnlyFailed,
}: {
  driver: LiveVehicle;
  onClose: () => void;
  onRefresh: () => void;
  showOnlyFailed: boolean;
}) {
  const [route, setRoute] = useState<Route | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!driver.routeId) return;
    setLoading(true);
    api
      .getRoute(driver.routeId)
      .then((r) => alive && setRoute(r))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [driver.routeId]);

  // Stops sin SECUENCIA del Excel (sequence === null) van al final;
  // dentro de cada grupo desempatamos por client_code para estabilidad.
  const stops = (route?.stops ?? []).slice().sort((a, b) => {
    if (a.sequence !== null && b.sequence !== null) {
      return a.sequence - b.sequence;
    }
    if (a.sequence !== null) return -1;
    if (b.sequence !== null) return 1;
    return (a.client_code ?? "").localeCompare(b.client_code ?? "");
  });
  const visible = showOnlyFailed
    ? stops.filter((s) => s.status === "failed" || s.status === "skipped")
    : stops;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-[420px] max-w-full bg-card h-full shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-semibold text-ink font-mono">{driver.plate}</div>
            <div className="text-xs text-sub">
              {driver.driverFullName ?? "Sin chofer"} · {driver.stopsDone}/
              {driver.stopsTotal} entregadas
              {driver.stopsFailed > 0 && ` · ${driver.stopsFailed} rechazos`}
              {" · "}avance {formatPct(driverProgress(driver) * 100)}
            </div>
            {showOnlyFailed && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-state-failed bg-state-failed/10 border border-state-failed/30 rounded px-1.5 py-0.5">
                <span>Filtro: solo rechazos</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-sub hover:text-ink text-xl leading-none">
            ×
          </button>
        </header>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-6 text-center text-sub text-sm">Cargando paradas…</div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-center text-sub text-sm">
              {showOnlyFailed ? "Sin paradas rechazadas." : "Sin paradas."}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {visible.map((s) => (
                <FailedStopRow key={s.id} stop={s} expanded={showOnlyFailed} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Stop row con detalle de rechazo ----------------

/// Fila del drawer de detalle. En modo "ver rechazados" (expanded=true)
/// muestra debajo el motivo del rechazo + notas + hora del intento, que
/// el supervisor necesita para entender por qué falló y decidir
/// reprogramar / contactar al cliente / etc.
function FailedStopRow({
  stop,
  expanded,
}: {
  stop: RouteStopRow;
  expanded: boolean;
}) {
  const exc = stop.exception;
  const showException =
    expanded && (stop.status === "failed" || stop.status === "skipped");
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
          style={{ backgroundColor: stopColor(stop.status) }}
        >
          {stopShortLabel(stop)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-ink truncate">{stopDisplayName(stop)}</div>
          <div className="text-xs text-sub flex items-center gap-1.5">
            <span>{stopHash(stop)}</span>
            <span>·</span>
            <span>{statusLabel(stop.status)}</span>
            {stop.client_code && (
              <>
                <span>·</span>
                <span className="font-mono">{stop.client_code}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {showException && (
        <div className="mt-2 ml-10 bg-state-failed/5 border-l-2 border-state-failed/40 pl-3 py-1.5 rounded-r">
          {exc ? (
            <>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="font-semibold text-state-failed uppercase tracking-wide">
                  Motivo
                </span>
                <span className="text-ink font-medium">
                  {exc.reason_label || exc.reason_code || "Sin motivo registrado"}
                </span>
              </div>
              {exc.notes && (
                <div className="mt-1 text-xs text-sub italic">
                  &ldquo;{exc.notes}&rdquo;
                </div>
              )}
              {exc.occurred_at && (
                <div className="mt-1 text-[10px] text-sub">
                  Registrado {formatHHMMSS(new Date(exc.occurred_at))}
                  {exc.lat != null && exc.lon != null && (
                    <span>
                      {" · "}
                      <span className="font-mono">
                        {exc.lat.toFixed(5)}, {exc.lon.toFixed(5)}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-sub italic">
              Sin detalle del rechazo (el chofer cerró sin completar el form).
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------- Pequeños helpers ----------------

function Kpi({
  label,
  value,
  unit,
  accent,
}: {
  label: string;
  value: string;
  /** Unidad opcional ("órdenes", "rutas") que se muestra después del número
   *  con tamaño reducido. Solo aplica cuando el valor es numérico — para
   *  porcentajes no se pasa (el "%" ya viene en el value). */
  unit?: string;
  accent?: boolean;
}) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wide text-sub">{label}</div>
      <div className={`font-mono ${accent ? "text-state-failed" : "text-ink"}`}>
        <span className="text-xl font-semibold">{value}</span>
        {unit && <span className="text-xs text-sub ml-1 font-normal">{unit}</span>}
      </div>
    </div>
  );
}

function Centered({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <Card padding="lg" className="max-w-md text-center">
        <h2 className="text-base text-ink font-semibold mb-2">{title}</h2>
        {children}
      </Card>
    </div>
  );
}

function formatHHMMSS(d?: Date): string {
  if (!d) return "—";
  return d.toLocaleTimeString("es-PE", { hour12: false });
}

function formatLocalDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function statusLabel(s: string): string {
  return (
    {
      delivered: "Entregada",
      partial: "Entrega parcial",
      failed: "Fallida",
      skipped: "Omitida",
      in_progress: "En curso",
      pending: "Pendiente",
    } as Record<string, string>
  )[s] ?? "Pendiente";
}

function stopColor(s: string): string {
  return (
    {
      delivered: "#2E7D32",
      partial: "#FB8C00",
      failed: "#D32F2F",
      skipped: "#D32F2F",
      in_progress: "#1976D2",
      pending: "#90A4AE",
    } as Record<string, string>
  )[s] ?? "#90A4AE";
}
